/**
 * F05 — battery-alert defaults for a NEW senior (launch spec).
 *
 * Two independent layers protect two different actors:
 *   - `voice_nag_enabled`  — Voice Assistant nudges the senior at 40%
 *   - `lowbat_sms_enabled` — caregiver gets an SMS at 25%
 *
 * Both are launch features, so both default ON. The one exception is the voice
 * nag on memory/cognitive profiles: a senior with cognitive impairment may not
 * respond reliably to a spoken nudge, so the caregiver has to enable it
 * deliberately (the drawer keeps the clinical note explaining why). The
 * caregiver SMS still defaults ON for those profiles — it protects the
 * caregiver, and their ability to act on it doesn't change with the senior's
 * profile.
 *
 * Keyed on the assessment profile, NOT on the layout id. `cb_1` is also the
 * recommended layout for physical/severe and custom/severe (see LAYOUT_MAPPING
 * in ./layouts), and those seniors respond to a voice nag normally — so
 * "CB-class layout" is not a safe proxy for "memory profile".
 *
 * Existing seniors are untouched by this module: it only supplies defaults for
 * rows that do not exist yet. A stored `false` stays `false`.
 */

/** Accepts every shape the profile reaches us in (wizard, Senior row, drawer prop). */
export type BatteryAlertProfile = string | null | undefined;

export interface BatteryAlertDefaults {
  voice_nag_enabled: boolean;
  lowbat_sms_enabled: boolean;
}

/**
 * The memory/confusion profile — stored as `cognitive`, shown to caregivers as
 * "Memory & Confusion".
 */
export function isMemoryProfile(profileType: BatteryAlertProfile): boolean {
  return profileType === 'cognitive';
}

/** Default battery-alert flags for a senior being created now. */
export function defaultBatteryAlertFlags(profileType: BatteryAlertProfile): BatteryAlertDefaults {
  return {
    voice_nag_enabled: !isMemoryProfile(profileType),
    lowbat_sms_enabled: true,
  };
}

/** Translated fragments for the dashboard tile's state line. */
export interface BatteryAlertStateLabels {
  on: string;
  off: string;
  voice: string;
  sms: string;
}

/**
 * The dashboard tile's state line — current battery-alert state, readable
 * without opening the drawer.
 *
 * Composed from fragments rather than being two fixed strings because the two
 * layers are independent: a memory profile defaults to voice OFF / SMS ON, so
 * both "On" and "Off" would misdescribe it. The line names whichever layers are
 * actually on:
 *
 *   both      → "On — voice 40% + SMS 25%"
 *   SMS only  → "On — SMS 25%"
 *   neither   → "Off"
 *
 * Returns `null` when either flag is still unknown (configuration row not
 * loaded), so the caller can keep its static subtitle instead of asserting
 * "Off" about a senior it hasn't read yet.
 */
export function formatBatteryAlertState(
  voiceOn: boolean | undefined,
  smsOn: boolean | undefined,
  labels: BatteryAlertStateLabels
): string | null {
  if (typeof voiceOn !== 'boolean' || typeof smsOn !== 'boolean') return null;
  const layers: string[] = [];
  if (voiceOn) layers.push(labels.voice);
  if (smsOn) layers.push(labels.sms);
  return layers.length ? `${labels.on} — ${layers.join(' + ')}` : labels.off;
}
